import { AppAlertsContext } from '@infl-ts/ui-app-alerts';
import { getLocale } from '@infl-ts/util-date';
import { useLanguage } from '@infl-ts/util-i18n-provider';
import { format, formatDistanceToNowStrict, fromUnixTime } from 'date-fns';
import * as E from 'fp-ts/Either';
import { absurd, constVoid, pipe } from 'fp-ts/function';
import * as t from 'io-ts';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
var AuthProvider = t.keyof({
  facebook: null,
  twitter: null,
  linkedin: null,
  apple: null
});
var AuthStatus = t.keyof({
  success: null,
  account_taken: null
});
var SocialAuthenticationParams = t.intersection([t.type({
  auth_provider: AuthProvider,
  auth_status: AuthStatus
}), t.partial({
  auth_expires_at: t.string
})]);

var alertMessage = function alertMessage(_ref) {
  var status = _ref.status,
      provider = _ref.provider,
      t = _ref.t;

  switch (status) {
    case 'success':
      return t('Your {{provider}} account is now connected.', {
        provider: provider
      });

    case 'account_taken':
      return t('That {{provider}} account is already taken.', {
        provider: provider
      });

    default:
      return absurd(status);
  }
};

var expirationDateMessage = function expirationDateMessage(_ref2) {
  var expireDate = _ref2.expireDate,
      provider = _ref2.provider,
      t = _ref2.t,
      locale = _ref2.locale;
  return t("Your {{provider}} connection will expire after {{expireDistance}}. You will need to reconnect your account on {{expireDate}}", {
    provider: provider,
    expireDistance: formatDistanceToNowStrict(expireDate, {
      unit: 'day',
      locale: locale
    }),
    expireDate: format(expireDate, 'MMM dd, yyyy', {
      locale: locale
    })
  });
};

var QueryAlerts = function QueryAlerts(_ref3) {
  var children = _ref3.children;

  var _useContext = useContext(AppAlertsContext),
      showAlert = _useContext.showAlert;

  var history = useHistory();

  var _useLanguage = useLanguage(),
      currentLang = _useLanguage.currentLang;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  useEffect(function () {
    var url = new URL(window.location.href);
    var params = url.searchParams;
    pipe(params, Object.fromEntries, function (params) {
      return SocialAuthenticationParams.decode(params);
    }, E.matchW(constVoid, function (_ref4) {
      var auth_provider = _ref4.auth_provider,
          auth_status = _ref4.auth_status,
          auth_expires_at = _ref4.auth_expires_at;
      var severity = auth_status === 'success' ? auth_status : 'error';
      var message = alertMessage({
        status: auth_status,
        provider: auth_provider,
        t: t
      });
      showAlert({
        severity: severity,
        message: message,
        duration: 'long'
      });

      if (auth_expires_at) {
        var expireDate = fromUnixTime(parseInt(auth_expires_at, 10));
        showAlert({
          severity: severity,
          message: expirationDateMessage({
            expireDate: expireDate,
            provider: auth_provider,
            t: t,
            locale: getLocale(currentLang)
          }),
          duration: 'long'
        });
      }

      params["delete"]('auth_provider');
      params["delete"]('auth_status');
      params["delete"]('auth_expires_at');
      history.replace("".concat(url.pathname, "?").concat(url.searchParams.toString()), {
        afterSocialAuth: true
      });
    }));
  }, []);
  return /*#__PURE__*/_jsx(_Fragment, {
    children: children
  });
};

export default QueryAlerts;